import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { Card, Button, Badge, Row, Col, Form, Spinner, Alert } from "react-bootstrap";

import { db } from "../../../../shared/libs/firebaseConfig";
import { useAuth } from "../../../../shared/auth/AuthProvider";
import {
  DEFAULT_AFFILIATE_COURSE_SETTINGS,
  normalizeAffiliateCommissionPercent,
} from "../../../../shared/utils/affiliateSchema";

const PAGE_SIZE = 12;

function normalizeText(s = "") {
  return String(s || "").toLowerCase().trim();
}

function formatPriceTHB(c) {
  // คอร์สเนติบัณฑิต ใช้หลายราคา ไม่แสดง 0 บาท
  if (c?.courseType === "thaibar") {
    return "ราคาตามแพ็คเกจ";
  }

  // ถ้ามี priceText ใช้ก่อน
  if (c?.priceText) return `฿${String(c.priceText)}`;

  // fallback: ใช้ price number
  const n = typeof c?.price === "number" ? c.price : Number(c?.price);
  if (!Number.isFinite(n)) return "—";
  return `฿${n.toLocaleString("th-TH")}`;
}

function normalizeRoles(role) {
  if (!role) return [];
  const arr = Array.isArray(role) ? role : [role];
  return arr.map((r) => String(r).trim().toLowerCase());
}

export default function AdminCourses() {
  const { user, userDoc, loading } = useAuth();

  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState("");

  const [qText, setQText] = useState("");
  const [status, setStatus] = useState("all"); // all | active | hidden
  const [featured, setFeatured] = useState("all"); // all | featured | normal

  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const isAdmin = useMemo(() => {
    const set = new Set(normalizeRoles(userDoc?.role));
    return set.has("admin");
  }, [userDoc]);

  const buildBaseQuery = () => {
    const parts = [
      orderBy("createdAt", "desc"),
      limit(PAGE_SIZE),
    ];

    if (status === "active") parts.unshift(where("isActive", "==", true));
    if (status === "hidden") parts.unshift(where("isActive", "==", false));

    if (featured === "featured") parts.unshift(where("isFeatured", "==", true));
    if (featured === "normal") parts.unshift(where("isFeatured", "==", false));

    return query(collection(db, "courses"), ...parts);
  };

  const loadFirst = async () => {
    setErr("");
    setLoadingList(true);
    setHasMore(true);
    setCursor(null);

    try {
      const q = buildBaseQuery();
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setItems(rows);
      setCursor(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
      setLoadingList(false);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "โหลดรายการคอร์สไม่สำเร็จ");
      setLoadingList(false);
    }
  };

  const loadMore = async () => {
    if (!cursor || !hasMore) return;
    setErr("");
    setLoadingMore(true);

    try {
      const parts = [];
      if (status === "active") parts.push(where("isActive", "==", true));
      if (status === "hidden") parts.push(where("isActive", "==", false));
      if (featured === "featured") parts.push(where("isFeatured", "==", true));
      if (featured === "normal") parts.push(where("isFeatured", "==", false));

      parts.push(orderBy("createdAt", "desc"));
      parts.push(startAfter(cursor));
      parts.push(limit(PAGE_SIZE));

      const q = query(collection(db, "courses"), ...parts);
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      setItems((prev) => [...prev, ...rows]);
      setCursor(snap.docs[snap.docs.length - 1] || cursor);
      setHasMore(snap.docs.length === PAGE_SIZE);
      setLoadingMore(false);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "โหลดเพิ่มไม่สำเร็จ");
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!user?.uid) return;
    loadFirst();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, status, featured]);

  const filteredByText = useMemo(() => {
    const t = normalizeText(qText);
    if (!t) return items;

    return items.filter((it) => {
      const title = normalizeText(it.title);
      const slug = normalizeText(it.slug);
      const cats = Array.isArray(it.categories) ? it.categories.join(" ") : "";
      const owner = normalizeText(it.ownerUid);
      const createdByName = normalizeText(it?.createdBy?.name || "");
      return (
        title.includes(t) ||
        slug.includes(t) ||
        normalizeText(cats).includes(t) ||
        owner.includes(t) ||
        createdByName.includes(t)
      );
    });
  }, [items, qText]);

  if (loading) return null;
  if (!user) return null;
  if (!isAdmin) return <Alert variant="warning">หน้านี้สำหรับ Admin เท่านั้น</Alert>;

  return (
    <div>
      <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h4 className="mb-0">คอร์สเรียนในระบบ</h4>
          <div className="text-muted small">
            แสดงรายการ <code>courses ทั้งหมด</code>
          </div>
        </div>

        <div className="d-flex gap-2">
          {/* <Button variant="outline-secondary" onClick={loadFirst} disabled={loadingList || loadingMore}>
            รีเฟรช
          </Button> */}
          {/* Admin ก็สร้างคอร์สได้ */}
          <Button as={Link} to="/account/creator/courses/new" variant="primary">
            + สร้างคอร์สใหม่
          </Button>
        </div>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      <Card className="border mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={6}>
              <Form.Label>ค้นหา</Form.Label>
              <Form.Control
                value={qText}
                onChange={(e) => setQText(e.target.value)}
                placeholder="ค้นหาจากชื่อ, slug, หมวดหมู่, ownerUid, createdBy.name..."
              />
            </Col>

            <Col md={3}>
              <Form.Label>สถานะ</Form.Label>
              <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="all">ทั้งหมด</option>
                <option value="active">Active</option>
                <option value="hidden">Hidden</option>
              </Form.Select>
            </Col>

            <Col md={3}>
              <Form.Label>แนะนำ</Form.Label>
              <Form.Select value={featured} onChange={(e) => setFeatured(e.target.value)}>
                <option value="all">ทั้งหมด</option>
                <option value="featured">Featured</option>
                <option value="normal">ไม่ Featured</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loadingList ? (
        <div className="py-5 text-center text-muted">
          <Spinner animation="border" size="sm" /> กำลังโหลดรายการ…
        </div>
      ) : filteredByText.length === 0 ? (
        <Card className="border">
          <Card.Body className="text-center py-5">
            <div className="mb-2 fw-semibold">ไม่พบคอร์ส</div>
            <div className="text-muted">ลองเปลี่ยนคำค้นหาหรือ filter</div>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Row className="g-3">
            {filteredByText.map((c) => {
              const cover = c.imageCoverUrl || c.coverUrl || c.cover || "";
              return (
                <Col key={c.id} xs={12} md={6} lg={4}>
                  <Card className="border h-100">
                    <div
                      style={{
                        width: "100%",
                        aspectRatio: "16/9",
                        background: "rgba(127,127,127,.08)",
                        overflow: "hidden",
                        borderTopLeftRadius: 6,
                        borderTopRightRadius: 6,
                      }}
                    >
                      {cover ? (
                        <img
                          src={cover}
                          alt={c.title || "cover"}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => (e.currentTarget.style.display = "none")}
                        />
                      ) : (
                        <div className="h-100 d-flex align-items-center justify-content-center text-muted small">
                          ไม่มีภาพปก
                        </div>
                      )}
                    </div>

                    <Card.Body className="d-flex flex-column">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <Badge bg={c.isActive ? "success" : "secondary"}>{c.isActive ? "Active" : "Hidden"}</Badge>
                        {c.isFeatured && (
                          <Badge bg="warning" text="dark">
                            Featured
                          </Badge>
                        )}
                      </div>

                      <div className="fw-semibold" style={{ lineHeight: 1.25 }}>
                        {c.title || "(ไม่มีชื่อ)"}
                    </div>

                    {/* ✅ ราคา */}
                    <div className="mt-1 d-flex align-items-center justify-content-between">
                        <div className="fw-semibold">
                            {formatPriceTHB(c)}
                        </div>

                        {/* (ถ้าอยากโชว์ type/level สั้นๆ ทางขวา) */}
                        {/* <span className="text-muted small">{c.level || ""}</span> */}
                    </div>

                      <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                        <Badge bg={c.affiliateEnabled ? "success" : "secondary"}>
                          Affiliate {c.affiliateEnabled ? "On" : "Off"}
                        </Badge>
                        <span className="text-muted small">
                          {normalizeAffiliateCommissionPercent(
                            c.affiliateCommissionPercent,
                            DEFAULT_AFFILIATE_COURSE_SETTINGS.affiliateCommissionPercent
                          )}
                          %
                        </span>
                      </div>

                     {/*  <div className="text-muted small mt-2">
                        slug: <code>{c.slug || "-"}</code>
                      </div> */}

                      <div className="text-muted small mt-2">
                        <code>{c?.createdBy?.name || c.ownerUid || "-"}</code>
                      </div>

                      {Array.isArray(c.categories) && c.categories.length > 0 && (
                        <div className="d-flex flex-wrap gap-1 mt-3">
                          {c.categories.slice(0, 4).map((x) => (
                            <Badge
                              key={x}
                              bg="light"
                              text="dark"
                              style={{ border: "1px solid rgba(0,0,0,.12)" }}
                            >
                              {x}
                            </Badge>
                          ))}
                          {c.categories.length > 4 && (
                            <Badge
                              bg="light"
                              text="dark"
                              style={{ border: "1px solid rgba(0,0,0,.12)" }}
                            >
                              +{c.categories.length - 4}
                            </Badge>
                          )}
                        </div>
                      )}

                      <div className="mt-auto pt-3 d-flex gap-2">
                        <Button
                          as={Link}
                          to={`/account/creator/courses/${c.id}`}
                          variant="outline-primary"
                          className="w-100"
                        >
                          แก้ไข
                        </Button>
                        <Button
                          as={Link}
                          to={`/courses/${c.slug || c.id}`}
                          variant="outline-secondary"
                          className="w-100"
                        >
                          ดูหน้าเว็บ
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>

          <div className="d-flex justify-content-center mt-4">
            {hasMore ? (
              <Button variant="outline-secondary" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? "กำลังโหลด..." : "โหลดเพิ่ม"}
              </Button>
            ) : (
              <div className="text-muted small">แสดงครบแล้ว</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
